import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Drawer,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { DeleteOutline as DeleteIcon, Close as CloseIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

export default function NotesDrawer({ 
  open, 
  item, 
  onClose, 
  onUpdate,
  onDelete,
  ownerOptions,
  statusOptions
}) {
  const [localItem, setLocalItem] = useState(null);
  const [newNote, setNewNote] = useState('');
  const notesEndRef = useRef(null);

  useEffect(() => {
    if (item) {
      setLocalItem({
        ...item,
        notes: Array.isArray(item.notes) ? item.notes : []
      });
    }
  }, [item]);

  useEffect(() => {
    scrollToBottom();
  }, [localItem?.notes]);

  const scrollToBottom = () => {
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (!localItem) return null;

  const handleFieldChange = (field, value) => {
    const updatedItem = {
      ...localItem,
      [field]: value
    };
    setLocalItem(updatedItem);
    onUpdate(updatedItem);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const updatedItem = {
        ...localItem,
        notes: Array.isArray(localItem.notes) ? [...localItem.notes] : [],
      };
      updatedItem.notes.push({
        id: Date.now().toString(),
        text: newNote.trim(),
        timestamp: new Date().toISOString()
      });
      setLocalItem(updatedItem);
      onUpdate(updatedItem);
      setNewNote('');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      let dateObj = date;
      if (date?.toDate) {
        dateObj = date.toDate();
      } else if (!(date instanceof Date)) {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date:', date);
        return '';
      }
      
      return format(dateObj, 'MMM d');
    } catch (error) {
      console.warn('Error formatting date:', error);
      return '';
    }
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption?.color || '#F4F2F2';
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '50%' }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pl: 3, pr: 1 }}>
        {/* Fixed Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              ACTION ITEM
            </Typography>
            <Box>
              <IconButton onClick={onDelete} sx={{ mr: 1, color: 'error.main' }}>
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <TextField
            fullWidth
            value={localItem.text}
            onChange={(e) => handleFieldChange('text', e.target.value)}
            variant="standard"
            multiline
            maxRows={4}
            sx={{ 
              '& .MuiInputBase-input': {
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '1.5'
              }
            }}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, width: '120px' }}>
              <Typography variant="subtitle2">Deadline</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {formatDate(localItem.deadline)}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={localItem.deadline}
                    onChange={(newDate) => {
                      if (newDate) {
                        handleFieldChange('deadline', newDate);
                      }
                    }}
                    renderInput={(props) => (
                      <TextField 
                        {...props} 
                        variant="standard" 
                        sx={{ 
                          width: '0px', 
                          border: 'none',
                          '& .MuiInput-underline:before': { borderBottom: 'none' },
                          '& .MuiInput-underline:after': { borderBottom: 'none' },
                        }} 
                      />
                    )}
                    components={{
                      OpenPickerIcon: () => (
                        <CalendarIcon 
                          sx={{ 
                            fontSize: '20px',
                            color: 'action.active',
                            opacity: 0.7,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />
                      )
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="subtitle2">Owner</Typography>
              <Select
                value={localItem.owner}
                onChange={(e) => handleFieldChange('owner', e.target.value)}
                variant="outlined"
                sx={{
                  height: '32px',
                  borderRadius: '32px',
                  backgroundColor: '#F4F2F2',
                  padding: '0px 16px',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }}
              >
                {ownerOptions.map((owner) => (
                  <MenuItem key={owner.email} value={owner.email}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="subtitle2">Status</Typography>
              <Select
                value={localItem.status}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                variant="outlined"
                sx={{
                  height: '32px',
                  borderRadius: '32px',
                  backgroundColor: getStatusColor(localItem.status),
                  padding: '0px 16px',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    sx={{
                      backgroundColor: option.color,
                      '&:hover': {
                        backgroundColor: option.color,
                        filter: 'brightness(0.95)'
                      }
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Notes Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Notes
          </Typography>
          <Box sx={{ mb: 2 }}>
            {(localItem.notes || []).map((note) => {
              let formattedTimestamp = '';
              try {
                const timestamp = note.timestamp instanceof Date ? note.timestamp : new Date(note.timestamp);
                if (!isNaN(timestamp.getTime())) {
                  formattedTimestamp = format(timestamp, "MMMM d, yyyy (h:mm a)");
                }
              } catch (error) {
                console.warn('Error formatting note timestamp:', error);
              }

              return (
                <Box key={note.id} sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {formattedTimestamp}
                  </Typography>
                  <Typography sx={{ pl: 2 }}>
                    {note.text}
                  </Typography>
                </Box>
              );
            })}
            <div ref={notesEndRef} />
          </Box>
        </Box>

        {/* Fixed Footer - Notes Input */}
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
          >
            Add note
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
} 